import type { BrowserViewport } from "@cmmd-forge/contracts";

export interface BrowserCaptureLayout {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly scale: number;
  readonly scaleX: number;
  readonly scaleY: number;
}

export interface BrowserCanvasRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface BrowserCanvasPoint {
  readonly x: number;
  readonly y: number;
}

export interface BrowserCanvasAnnotationAnchor {
  readonly left: number;
  readonly top: number;
  readonly transform: "none";
}

export function resolveBrowserCaptureLayout(input: {
  readonly containerWidth: number;
  readonly containerHeight: number;
  readonly viewport: BrowserViewport | null | undefined;
}): BrowserCaptureLayout | null {
  const viewport = input.viewport;
  if (
    !viewport ||
    input.containerWidth <= 0 ||
    input.containerHeight <= 0 ||
    viewport.width <= 0 ||
    viewport.height <= 0
  ) {
    return null;
  }

  const scaleX = input.containerWidth / viewport.width;
  const scaleY = input.containerHeight / viewport.height;
  return {
    left: 0,
    top: 0,
    width: input.containerWidth,
    height: input.containerHeight,
    scale: Math.min(scaleX, scaleY),
    scaleX,
    scaleY,
  };
}

export function capturePointToViewportPoint(input: {
  readonly point: BrowserCanvasPoint;
  readonly layout: BrowserCaptureLayout;
  readonly viewport: BrowserViewport;
}): BrowserCanvasPoint {
  const x = (input.point.x - input.layout.left) / input.layout.scaleX;
  const y = (input.point.y - input.layout.top) / input.layout.scaleY;
  return {
    x: Math.max(0, Math.min(input.viewport.width, Math.round(x))),
    y: Math.max(0, Math.min(input.viewport.height, Math.round(y))),
  };
}

export function viewportRectToCaptureRect(input: {
  readonly rect: BrowserCanvasRect;
  readonly layout: BrowserCaptureLayout;
}): BrowserCanvasRect {
  return {
    x: input.layout.left + input.rect.x * input.layout.scaleX,
    y: input.layout.top + input.rect.y * input.layout.scaleY,
    width: input.rect.width * input.layout.scaleX,
    height: input.rect.height * input.layout.scaleY,
  };
}

export function viewportPointToCapturePoint(input: {
  readonly point: BrowserCanvasPoint;
  readonly layout: BrowserCaptureLayout;
}): BrowserCanvasPoint {
  return {
    x: input.layout.left + input.point.x * input.layout.scaleX,
    y: input.layout.top + input.point.y * input.layout.scaleY,
  };
}

export function resolveBrowserCanvasDraftRect(input: {
  readonly start: BrowserCanvasPoint | null;
  readonly current: BrowserCanvasPoint | null;
}): BrowserCanvasRect | null {
  if (!input.start || !input.current) {
    return null;
  }
  return {
    x: Math.min(input.start.x, input.current.x),
    y: Math.min(input.start.y, input.current.y),
    width: Math.abs(input.current.x - input.start.x),
    height: Math.abs(input.current.y - input.start.y),
  };
}

export function resolveBrowserCanvasCommitTarget(input: {
  readonly point: BrowserCanvasPoint;
  readonly dragStart: BrowserCanvasPoint;
}): { readonly point: BrowserCanvasPoint; readonly rect?: BrowserCanvasRect } {
  const width = Math.abs(input.point.x - input.dragStart.x);
  const height = Math.abs(input.point.y - input.dragStart.y);
  if (width > 6 || height > 6) {
    return {
      point: input.point,
      rect: {
        x: Math.min(input.dragStart.x, input.point.x),
        y: Math.min(input.dragStart.y, input.point.y),
        width,
        height,
      },
    };
  }
  return { point: input.point };
}

export function resolveBrowserCanvasSelection(input: {
  readonly selectedTarget:
    | {
        readonly url: string;
        readonly point?: BrowserCanvasPoint | undefined;
        readonly rect?: BrowserCanvasRect | undefined;
      }
    | null
    | undefined;
  readonly url: string | null;
  readonly draftRect: BrowserCanvasRect | null;
}): {
  readonly rect: BrowserCanvasRect | null;
  readonly point: BrowserCanvasPoint | null;
} {
  if (input.selectedTarget?.url === input.url) {
    return {
      rect: input.selectedTarget.rect ?? null,
      point: input.selectedTarget.point ?? null,
    };
  }
  return { rect: input.draftRect, point: null };
}

export function resolveAnnotationAnchor(input: {
  readonly layout: BrowserCaptureLayout | null;
  readonly point: BrowserCanvasPoint | null;
  readonly rect: BrowserCanvasRect | null;
}): BrowserCanvasAnnotationAnchor | null {
  const targetX = input.rect ? input.rect.x + input.rect.width : input.point?.x;
  const targetY = input.rect ? input.rect.y + input.rect.height : input.point?.y;
  if (targetX === undefined || targetY === undefined) {
    return null;
  }
  const boundedLeft = Math.max(
    8,
    Math.min(targetX + 12, (input.layout?.left ?? 0) + (input.layout?.width ?? 0) - 296),
  );
  const boundedTop = Math.max(
    8,
    Math.min(targetY + 12, (input.layout?.top ?? 0) + (input.layout?.height ?? 0) - 144),
  );
  return {
    left: boundedLeft,
    top: boundedTop,
    transform: "none",
  };
}
